/**
 * SourceRow - one document, in the one shape every document uses.
 *
 * ONE anatomy, borrowed from the review documents list:
 *
 *     [ glyph 26px │ title + one meta line │ right slot ]
 *
 * All three are always present and always the same height, so the list can be
 * scanned down. The old tab had a different row per tab - a thumbnail card
 * here, a five-column table there, relevance appearing and disappearing - and
 * an evaluating document grew an extra line of copy, so rows physically moved
 * while the AI worked.
 *
 * Everything conditional is expressed INSIDE a fixed slot instead:
 *
 *   • evaluation swaps the GLYPH for a loader and the relevance chip for a
 *     shimmer - the row does not change size;
 *   • relevance ("Cited" / "Related") and the origin pill share one
 *     right-hand slot of fixed width, so a case file with no relevance leaves
 *     the same gap a law with one occupies;
 *   • the rationale the AI gave rides on the single meta line, replacing the
 *     facts rather than adding a second line.
 *
 * The row OPENS the document - reading is what this list is for. Selecting is
 * the checkbox, which lives in the glyph's slot and takes it over on hover.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import {
  PiBuildingsLight,
  PiCloudArrowDownLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiGavelLight,
  PiScalesLight,
  PiScrollLight,
  PiTrashLight,
} from 'react-icons/pi';
import BrandLoader from '../../../shared/brand/BrandLoader';
import RelevanceBadge from '../../RelevanceBadge';
import OriginPill from './OriginPill';
import { CORPUS_LABELS, MatterSource, SourceCorpus } from './sourceModel';

/** Corpus → glyph. One icon per corpus, so the chip and the row agree. */
const CORPUS_ICON: Record<SourceCorpus, React.ReactNode> = {
  case_file: <PiFileTextLight />,
  legislation: <PiScalesLight />,
  case_law: <PiGavelLight />,
  preparatory_works: <PiScrollLight />,
  regulations: <PiScrollLight />,
  agency_decisions: <PiBuildingsLight />,
  data_sources: <PiDatabaseLight />,
};

/** Relevance is only a claim the retriever can make about a retrieved source. */
const RELEVANCE_CORPORA: SourceCorpus[] = [
  'legislation',
  'case_law',
  'preparatory_works',
  'regulations',
  'agency_decisions',
];

interface Props {
  item: MatterSource;
  /** The AI is scoring this one right now (PubSub state or a pending status). */
  isEvaluating: boolean;
  isSelected: boolean;
  selectable: boolean;
  onOpen: () => void;
  onSelect: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  isDeleting?: boolean;
  /** Staggered reveal, as the review and research lists do. */
  index?: number;
}

const SourceRow: React.FC<Props> = ({
  item,
  isEvaluating,
  isSelected,
  selectable,
  onOpen,
  onSelect,
  onDelete,
  onDownload,
  isDeleting = false,
  index = 0,
}) => {
  const { t } = useTranslation();

  // The extension is noise in the title and information in the meta line.
  const displayName = (item.name || t('Untitled')).replace(/\.(docx?|pdf|xlsx?|pptx?|txt|html?)$/i, '');

  /**
   * The single meta line.
   *
   * The AI's rationale wins when there is one: "why is this here" beats a
   * size in kilobytes. Otherwise it is the plain facts.
   */
  const facts: string[] = [t(CORPUS_LABELS[item.corpus])];
  if (item._datasourceName) facts.push(item._datasourceName);
  if (item.corpus === 'case_file' && item.size) {
    facts.push(`${(item.size / 1024).toFixed(0)} KB`);
  }
  if (item.country) facts.push(item.country);
  if (Array.isArray(item.relevantSections) && item.relevantSections.length > 0) {
    facts.push(t('{{count}} sections', { count: item.relevantSections.length }));
  }
  const meta = isEvaluating
    ? t('AI is analysing this document for relevance...')
    : item.justification || facts.join(' · ');

  const showRelevance = item.origin === 'ai_found' && RELEVANCE_CORPORA.includes(item.corpus);

  return (
    <div
      data-testid="source-row"
      data-corpus={item.corpus}
      data-origin={item.origin}
      data-selected={isSelected}
      style={{ '--reveal-delay': `${Math.min(index, 8) * 30}ms` } as React.CSSProperties}
      title={item.name}
      className={clsx(
        'group relative reveal-item w-full rounded-xl mb-0.5 overflow-hidden',
        'transition-all duration-200 ease-slide',
        isDeleting && 'opacity-50 pointer-events-none',
        isSelected
          ? 'bg-champagne/10 ring-1 ring-inset ring-champagne/50'
          : 'hover:bg-surface/80 hover:shadow-md hover:ring-1 hover:ring-hairline-soft'
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'absolute start-0 top-0 bottom-0 w-[3px] bg-champagne',
          'transition-opacity duration-200 ease-slide',
          isSelected ? 'opacity-100' : 'opacity-0'
        )}
      />

      <button
        type="button"
        data-testid="source-row-open"
        onClick={onOpen}
        className="w-full text-start px-3 py-2.5"
      >
        <div className="grid grid-cols-[26px_1fr_auto] items-center gap-3">
          {/* ── Glyph slot: icon → checkbox on hover → loader while evaluating ── */}
          <span className="relative w-[26px] h-[26px] flex-shrink-0">
            <span
              className={clsx(
                'absolute inset-0 rounded-full border border-hairline-soft bg-surface-sunken/70',
                'flex items-center justify-center text-fg-muted [&>svg]:h-3.5 [&>svg]:w-3.5',
                'transition-opacity duration-200 ease-slide',
                // The checkbox takes this slot over; nothing moves.
                selectable && (isSelected ? 'opacity-0' : 'group-hover:opacity-0')
              )}
            >
              {isDeleting || isEvaluating ? (
                <BrandLoader size="xs" className="!h-3.5 !w-3.5" />
              ) : (
                CORPUS_ICON[item.corpus]
              )}
            </span>

            {selectable && (
              <span
                role="checkbox"
                aria-checked={isSelected}
                aria-label={t('Select')}
                tabIndex={0}
                data-testid="source-row-select"
                onClick={(e) => { e.stopPropagation(); onSelect(); }}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onSelect(); }
                }}
                className={clsx(
                  'absolute inset-0 flex items-center justify-center cursor-pointer',
                  'transition-opacity duration-200 ease-slide',
                  isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
              >
                <input
                  type="checkbox"
                  tabIndex={-1}
                  readOnly
                  checked={isSelected}
                  className="h-3.5 w-3.5 rounded border-hairline text-champagne
                             focus:ring-0 focus:ring-offset-0 pointer-events-none"
                />
              </span>
            )}
          </span>

          {/* ── Title + ONE meta line ─────────────────────────────── */}
          <div className="min-w-0">
            <p
              className={clsx(
                'text-[13px] truncate leading-snug transition-colors duration-200 ease-slide',
                isSelected ? 'font-semibold text-fg-strong' : 'font-medium text-fg-body'
              )}
            >
              {displayName}
            </p>
            <p
              className={clsx(
                'text-[11px] truncate mt-0.5',
                isEvaluating ? 'text-info-500 dark:text-info-400 animate-fadePulse' : 'text-fg-muted'
              )}
            >
              {meta}
            </p>
          </div>

          {/* ── Right slot: relevance, then origin. Fixed width, so a row
                without relevance leaves the gap one with it occupies. ──── */}
          <div className="flex items-center justify-end gap-1.5 flex-shrink-0 w-[152px]
                          transition-[margin] duration-200 ease-slide group-hover:me-14">
            {isEvaluating ? (
              <span
                data-testid="relevance-pending"
                className="h-5 w-14 rounded bg-info-100 dark:bg-info-900/30 animate-fadePulse"
              />
            ) : showRelevance ? (
              <RelevanceBadge isTargeted={item.isTargeted} className="!text-[10px] !px-1.5 !py-0.5" />
            ) : null}
            <OriginPill origin={item.origin} />
          </div>
        </div>
      </button>

      {/* Row actions - revealed on hover, in the space the right slot vacates. */}
      <div
        className="absolute end-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5
                   opacity-0 group-hover:opacity-100 focus-within:opacity-100
                   transition-opacity duration-200 ease-slide"
      >
        {onDownload && (
          <Tooltip title={t('Download')} placement="top">
            <button
              type="button"
              data-testid="source-row-download"
              aria-label={t('Download')}
              onClick={(e) => { e.stopPropagation(); onDownload(); }}
              className="p-1 rounded-md text-fg-muted hover:text-champagne hover:bg-surface-sunken
                         transition-colors duration-200 ease-slide"
            >
              <PiCloudArrowDownLight className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={t('Remove')} placement="top">
            <button
              type="button"
              data-testid="source-row-delete"
              aria-label={t('Remove')}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1 rounded-md text-fg-muted hover:text-danger-600 hover:bg-surface-sunken
                         transition-colors duration-200 ease-slide"
            >
              <PiTrashLight className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default SourceRow;
